import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  onAuthStateChanged,
  signOut as firebaseSignOut,
  type User,
} from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../firebase';
import type { UserDoc } from '../types';

/**
 * Tracks the Firebase auth user and the matching `users/{uid}` doc.
 * Strict Firestore rules let a user read their OWN doc, which is enough
 * for the role gate.
 *
 * `loading` flips to false once we've heard back from BOTH the auth
 * stream and (if there's a user) the user-doc snapshot, so consumers
 * never render the wrong UI on a stale state.
 */
interface AuthCtx {
  user: User | null;
  userDoc: UserDoc | null;
  loading: boolean;
  isStaff: boolean;
  isAdmin: boolean;
  signOut: () => Promise<void>;
}

const Ctx = createContext<AuthCtx | undefined>(undefined);

const STAFF_ROLES = new Set<UserDoc['role']>(['support', 'admin']);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [userDoc, setUserDoc] = useState<UserDoc | null>(null);
  const [authReady, setAuthReady] = useState(false);
  const [docReady, setDocReady] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setAuthReady(true);
      if (!u) {
        setUserDoc(null);
        setDocReady(true);
      } else {
        setDocReady(false);
      }
    });
    return unsub;
  }, []);

  useEffect(() => {
    if (!user) return;
    const unsub = onSnapshot(
      doc(db, 'users', user.uid),
      (snap) => {
        if (snap.exists()) {
          setUserDoc({ uid: snap.id, ...(snap.data() as Omit<UserDoc, 'uid'>) });
        } else {
          setUserDoc(null);
        }
        setDocReady(true);
      },
      (err) => {
        console.warn('[auth] users/me snapshot error', err);
        setUserDoc(null);
        setDocReady(true);
      },
    );
    return unsub;
  }, [user]);

  const value = useMemo<AuthCtx>(
    () => ({
      user,
      userDoc,
      loading: !authReady || !docReady,
      isStaff: !!userDoc && STAFF_ROLES.has(userDoc.role),
      isAdmin: userDoc?.role === 'admin',
      signOut: () => firebaseSignOut(auth),
    }),
    [user, userDoc, authReady, docReady],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth used outside AuthProvider');
  return v;
}
